import type { RefObject } from 'react'
import { useEffect } from 'react'

type UseAutoFocusOnOpenProps = {
  isOpen: boolean
  menuRef: RefObject<HTMLElement | null>
  backBtnRef: RefObject<HTMLElement | null>
  selectedSubmenu?: string
  animationDuration?: number
}

/**
 * When `isOpen` becomes true, wait `animationDuration` ms,
 * then focus `backBtnRef` if `selectedSubmenu` is non‐empty,
 * otherwise focus the first focusable element inside `menuRef`.
 */
export const useAutoFocusOnOpen = ({
  isOpen,
  menuRef,
  backBtnRef,
  selectedSubmenu,
  animationDuration = 300,
}: UseAutoFocusOnOpenProps) => {
  useEffect(() => {
    if (!isOpen) {
      return
    }

    const timer = setTimeout(() => {
      if (!menuRef.current) {
        return
      }

      // if a submenu is open, focus its back button
      if (selectedSubmenu && backBtnRef.current) {
        backBtnRef.current.focus()
        return
      }

      // otherwise focus the very first focusable control in the menu
      const firstFocusable = menuRef.current.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      )
      if (firstFocusable) {
        firstFocusable.focus()
      }
    }, animationDuration)

    return () => clearTimeout(timer)
  }, [isOpen, selectedSubmenu, menuRef, backBtnRef, animationDuration])
}
